import React, { Suspense, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Star, Clock, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import CarouselAnalytics from './CarouselAnalytics';
import LazyImage from './LazyImage';
import { useCarouselPrefetch } from '@/utils/carouselPrefetch';
import { LanguageBadgeList } from '@/components/ui/language-badge';
import SocialShare from '@/components/ui/social-share';
import { formatWeeklyHours } from '@/hooks/useWeeklyHours';
import { useFeaturedPsychologistsWithAvailability } from '@/hooks/usePsychologistsWithAvailability';
import '@/styles/carousel-enhancements.css';
import { useRealtimeAllProfilesUpdates } from '@/hooks/useRealtimeProfileUpdates';

const FeaturedPsychologistsCarousel = () => {
  const queryClient = useQueryClient();
  const {
    prefetchNext,
    getCached
  } = useCarouselPrefetch();

  const {
    data: psychologists = [],
    isLoading,
    error,
    refetch
  } = useFeaturedPsychologistsWithAvailability();

  // 🔥 REAL-TIME UPDATES para todos os perfis
  const { isConnected } = useRealtimeAllProfilesUpdates();

  console.log('🎠 [FeaturedPsychologistsCarousel] COMPONENT RENDER - Psychologists data received:', {
    count: psychologists.length,
    realtimeConnected: isConnected,
    psychologists: psychologists.map(p => ({
      id: p.id,
      name: p.name,
      email: p.email,
      rawData: p
    }))
  });

  // Force refresh when cache updates
  useEffect(() => {
    const interval = setInterval(() => {
      console.log('🔄 Auto-refreshing carousel data...');
      refetch();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [refetch]);

  // Enhanced analytics tracking
  const trackCarouselEvent = useCallback((psychologistId: string, action: string, position?: number) => {
    const psychologist = psychologists.find(p => p.id === psychologistId);
    if (action === 'impression' && psychologists.length > 0) {
      import('@/components/analytics/EnhancedGoogleAnalytics').then(({
        trackCarouselImpression
      }) => {
        trackCarouselImpression(psychologists.map(p => ({
          id: p.id,
          name: p.name
        })));
      });
    } else if (action === 'click' && psychologist && position !== undefined) {
      import('@/components/analytics/EnhancedGoogleAnalytics').then(({
        trackCarouselClick
      }) => {
        trackCarouselClick(psychologistId, psychologist.name, position, action as any);
      });
    }

    console.log('🎯 Enhanced Carousel Analytics:', {
      psychologistId,
      action,
      position
    });
  }, [psychologists]);

  // Track performance metrics
  useEffect(() => {
    if (psychologists.length > 0) {
      const loadTime = performance.now();
      import('@/components/analytics/EnhancedGoogleAnalytics').then(({
        trackCarouselPerformance
      }) => {
        trackCarouselPerformance(loadTime, false);
      });
    }
  }, [psychologists]);

  // Set up intersection observer for prefetching
  useEffect(() => {
    if (!psychologists.length) return;
    const psychologistIds = psychologists.map(p => p.id);

    psychologistIds.slice(0, 3).forEach((id, index) => {
      prefetchNext(index, psychologistIds);
    });

    const observers: IntersectionObserver[] = [];
    psychologists.forEach((psychologist, index) => {
      const element = document.querySelector(`[data-psychologist-id="${psychologist.id}"]`);
      if (element && index < psychologists.length - 2) {
        const observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              prefetchNext(index, psychologistIds);
              observer.unobserve(entry.target);
            }
          });
        }, {
          threshold: 0.5
        });
        observer.observe(element);
        observers.push(observer);
      }
    });

    trackCarouselEvent('carousel', 'impression');
    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, [psychologists, prefetchNext, trackCarouselEvent]);

  // Invalidar cache quando necessário
  const invalidateCache = () => {
    queryClient.invalidateQueries({ queryKey: ['featured-psychologists-carousel-with-availability'] });
    queryClient.invalidateQueries({ queryKey: ['weekly-hours'] });
  };

  console.log('🎠 FeaturedPsychologistsCarousel - Component render:', {
    psychologists: psychologists?.length,
    isLoading,
    error
  });
  
  if (error) {
    console.error('🎠 FeaturedPsychologistsCarousel - Error state:', error);
    return <div className="text-center py-8">
        <p className="text-muted-foreground">
          Não foi possível carregar os psicólogos em destaque. Tente novamente mais tarde.
        </p>
        <Button variant="outline" size="sm" onClick={invalidateCache} className="mt-2">
          Tentar novamente
        </Button>
      </div>;
  }
  
  if (isLoading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => <Card key={i} className="h-64 animate-pulse">
            <CardContent className="p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 rounded"></div>
                <div className="h-3 bg-gray-200 rounded w-5/6"></div>
              </div>
            </CardContent>
          </Card>)}
      </div>;
  }
  
  if (!psychologists || psychologists.length === 0) {
    console.log('🎠 FeaturedPsychologistsCarousel - No psychologists found');
    return <div className="text-center py-12">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <Star className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Nenhum psicólogo em destaque
          </h3>
          <p className="text-gray-600">
            No momento não temos psicólogos destacados com disponibilidade. Volte em breve para ver nossos profissionais em destaque.
          </p>
          <Button variant="outline" size="sm" onClick={invalidateCache} className="mt-4">
            Atualizar
          </Button>
        </div>
      </div>;
  }
  
  const getInitials = (name: string) => {
    return name.split(" ").map(n => n[0]).join("").toUpperCase();
  };

  // Hook to fetch pricing data for a psychologist
  const usePsychologistPricing = (psychologistId: string) => {
    return useQuery({
      queryKey: ['psychologist-pricing', psychologistId],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('psychologist_pricing')
          .select('duration_minutes, price')
          .eq('psychologist_id', psychologistId)
          .eq('is_active', true)
          .order('duration_minutes', { ascending: true });
        
        if (error) throw error;
        return data || [];
      },
    });
  };

  // Helper function to get available duration options with pricing
  const getDurationOptions = (psychologist: any, pricingData: any[] = []) => {
    if (pricingData.length > 0) {
      return pricingData.map(pricing => ({
        duration: pricing.duration_minutes,
        price: pricing.price
      }));
    }
    
    // Fallback to default pricing if no specific durations are set
    return [{
      duration: 50,
      price: psychologist.price || 150
    }];
  };

  // PRD v86: Enhanced language display component
  const LanguageDisplayV86 = ({
    languages
  }: {
    languages: string[];
  }) => {
    if (!languages || languages.length === 0) return null;
    const visibleLanguages = languages.slice(0, 3);
    const hiddenCount = Math.max(0, languages.length - 3);
    return <div className="flex flex-wrap gap-1">
        {visibleLanguages.map((language, index) => <Badge key={index} variant="secondary" className="text-xs px-2 py-1">
            {language}
          </Badge>)}
        {hiddenCount > 0 && <Badge variant="outline" className="text-xs px-2 py-1 cursor-help border-dashed" title={`Outros idiomas: ${languages.slice(3).join(', ')}`} aria-label={`+${hiddenCount} idiomas adicionais`}>
            +{hiddenCount}
          </Badge>}
      </div>;
  };

  // Updated duration display component to match the image layout
  const DurationDisplayV86 = ({
    durationOptions
  }: {
    durationOptions: any[];
  }) => {
    if (!durationOptions || durationOptions.length === 0) return null;
    
    return <div>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">DURAÇÕES</p>
        <div className="flex flex-wrap justify-center gap-1 text-xs">
          {durationOptions.map((option, index) => (
            <span 
              key={index}
              className="text-gray-600"
            >
              {option.duration}min - R$ {option.price?.toFixed(0) || '150'}
              {index < durationOptions.length - 1 && <span className="mx-1">;</span>}
            </span>
          ))}
        </div>
      </div>;
  };

  // Individual psychologist card component
  const PsychologistCarouselCard = ({ psychologist, index }: { psychologist: any, index: number }) => {
    console.log('🃏 [PsychologistCarouselCard] CARD RENDER - Rendering card for:', {
      id: psychologist.id,
      name: psychologist.name,
      email: psychologist.email,
      fullPsychologistObject: psychologist
    });
    
    const { data: pricingData = [] } = usePsychologistPricing(psychologist.id);
    const durationOptions = getDurationOptions(psychologist, pricingData);

    return (
      <Card 
        key={`${psychologist.id}-${psychologist.name}`}
        className="border-2 border-cyan-100 relative" 
        data-psychologist-id={psychologist.id} 
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          import('@/components/analytics/EnhancedGoogleAnalytics').then(({
            trackCarouselHeatmapData
          }) => {
            trackCarouselHeatmapData(`card-${psychologist.id}`, x, y, psychologist.id);
          });
        }}
      >
        <div className="absolute top-3 right-3 z-10">
          <SocialShare 
            psychologistId={psychologist.id}
            psychologistName={psychologist.name}
          />
        </div>

        <CardContent className="p-6">
          <div className="flex items-center space-x-4 mb-4">
            <div className="relative">
              {psychologist.photo_url ? 
                <LazyImage src={psychologist.photo_url} alt={psychologist.name} className="w-12 h-12 rounded-full object-cover" /> : 
                <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center text-cyan-600 font-semibold">
                  {getInitials(psychologist.name)}
                </div>
              }
              <Badge className="absolute -top-1 -right-1 bg-cyan-500 text-white text-xs px-1">
                ✨
              </Badge>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 truncate">
                {psychologist.name}
              </h3>
              <p className="text-sm text-gray-600">CRP: {psychologist.crp}</p>
            </div>
          </div>

          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-1">
                <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                <span className="text-sm font-medium">
                  {psychologist.rating ? psychologist.rating.toFixed(1) : 'N/A'}
                </span>
              </div>
            </div>

            {psychologist.short_bio && <p className="text-sm text-gray-600 line-clamp-2">
                {psychologist.short_bio}
              </p>}

            {psychologist.specialties && psychologist.specialties.length > 0 && <div className="flex flex-wrap gap-1">
                {psychologist.specialties.slice(0, 2).map((specialty, index) => <Badge key={index} className="bg-green-500 hover:bg-green-600 text-white text-xs">
                    {specialty}
                  </Badge>)}
                {psychologist.specialties.length > 2 && <Badge className="bg-green-500 hover:bg-green-600 text-white text-xs">
                    +{psychologist.specialties.length - 2}
                  </Badge>}
              </div>}

            {/* PRD v86: Enhanced Languages Section */}
            {psychologist.languages && psychologist.languages.length > 0 && <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">IDIOMAS</p>
                <div className="flex flex-wrap gap-1">
                  {psychologist.languages.slice(0, 3).map((language, index) => <Badge key={index} className="bg-purple-500 hover:bg-purple-600 text-white text-xs px-2 py-1">
                      {language}
                    </Badge>)}
                  {psychologist.languages.length > 3 && <Badge className="bg-purple-500 hover:bg-purple-600 text-white text-xs px-2 py-1 border-dashed cursor-help" title={`Outros idiomas: ${psychologist.languages.slice(3).join(', ')}`} aria-label={`+${psychologist.languages.length - 3} idiomas adicionais`}>
                      +{psychologist.languages.length - 3}
                    </Badge>}
                </div>
              </div>}

            {/* PRD v128: Weekly availability hours */}
            <div className="bg-blue-50 p-2 rounded-md">
              <div className="flex items-center space-x-1 text-sm">
                <Clock className="h-3 w-3 text-blue-600" />
                <span className="text-blue-700 font-medium">
                  {formatWeeklyHours(psychologist.weekly_hours_minutes || 0)}
                </span>
              </div>
            </div>

            {/* Updated Duration Section to match image layout */}
            <DurationDisplayV86 durationOptions={durationOptions} />
          </div>

          <div className="flex space-x-2">
            <Button asChild variant="outline" size="sm" className="flex-1" onClick={() => trackCarouselEvent(psychologist.id, 'view_profile', index + 1)}>
              <Link to={`/psychologists/${psychologist.id}`}>
                <MessageCircle className="w-4 h-4 mr-1" />
                Ver Perfil
              </Link>
            </Button>
            <Button asChild size="sm" className="flex-1 bg-cyan-600 hover:bg-cyan-700" onClick={() => trackCarouselEvent(psychologist.id, 'book_appointment', index + 1)}>
              <Link to={`/schedule/step1?psychologist=${psychologist.id}`}>
                Agendar
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };
  
  
  return <Suspense fallback={<div>Carregando...</div>}>
      <div className="space-y-6">
        <CarouselAnalytics psychologists={psychologists} />
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {psychologists.map((psychologist, index) => (
            <PsychologistCarouselCard 
              key={`${psychologist.id}-${psychologist.name}-${Date.now()}`}
              psychologist={psychologist}
              index={index}
            />
          ))}
        </div>
      </div>
    </Suspense>;
};

export default FeaturedPsychologistsCarousel;
